import { useCallback, useEffect, useState } from "react";

/**
 * Browser-native event type with the two non-standard fields the hook needs.
 * `BeforeInstallPromptEvent` is not part of the standard lib.dom.d.ts in
 * every TypeScript build, so we declare a minimal interface that matches the
 * fields the browser actually adds at runtime.
 */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

/**
 * Minimal shape of an entry from `navigator.getInstalledRelatedApps()`.
 * We only care about the `platform` field: a `webapp` entry means this PWA
 * is installed on the device.
 */
interface RelatedApplication {
  platform: string;
}

/** Returns true when any related app is this PWA itself. */
function hasInstalledPwa(apps: RelatedApplication[]): boolean {
  return apps.some(app => app.platform === "webapp");
}

const SESSION_DECLINED_KEY = "pwa:install-declined";

/**
 * Safe accessors for `sessionStorage`. Browsers in private mode or with
 * storage disabled can throw on every call; the hook must keep working in
 * those contexts using an in-memory fallback.
 */
function safeSessionGet(): string | null {
  try {
    return window.sessionStorage.getItem(SESSION_DECLINED_KEY);
  } catch {
    return null;
  }
}

function safeSessionSet(value: string): void {
  try {
    window.sessionStorage.setItem(SESSION_DECLINED_KEY, value);
  } catch {
    /* fall through to in-memory only */
  }
}

/**
 * Standalone display-mode detector. The query string targets the standard
 * media feature that the spec uses to flag installed PWAs; iOS Safari does
 * not implement this check, so we return `false` if matchMedia is missing.
 */
function detectInstalled(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(display-mode: standalone)").matches;
  } catch {
    return false;
  }
}

export interface PwaInstallState {
  /** True while we hold a `beforeinstallprompt` event that hasn't been acted on. */
  isInstallable: boolean;
  /** True if the app is already running in standalone (installed) display mode. */
  isInstalled: boolean;
  /** True if the user dismissed the prompt in this session (or storage said so). */
  declined: boolean;
  /** Triggers the saved `BeforeInstallPromptEvent.prompt()`; clears installable state. */
  promptInstall: () => Promise<void>;
  /** Records the user's dismissal; clears the saved event so we don't re-prompt. */
  dismissInstall: () => void;
}

/**
 * Captures the browser's `beforeinstallprompt` event and exposes a small API
 * for the install banner. Designed to be called once near the root of the
 * app (mounted in `AppLayout`).
 *
 * Behavior contract (from spec PWA-007):
 *   - The first `beforeinstallprompt` is captured and stashed; subsequent
 *     firings are ignored to avoid re-prompting.
 *   - Dismissing sets a session-scoped flag (survives remounts, clears on
 *     tab close) so the banner does not come back this session.
 *   - Once `promptInstall()` runs we drop the event reference: per the spec,
 *     the prompt is single-use.
 *   - `isInstalled` is detected three ways: `display-mode: standalone`
 *     (running inside the installed app), `navigator.getInstalledRelatedApps()`
 *     (installed, even from a plain browser tab — Chromium only), and the
 *     `appinstalled` event (fires right after the user accepts the prompt).
 *   - When already installed, `beforeinstallprompt` is not prevented or
 *     captured, so Chrome never logs "Banner not shown" for installed users.
 */
export function usePwaInstall(): PwaInstallState {
  const [installEvent, setInstallEvent] = useState<InstallPromptEvent | null>(null);
  // Read the initial declined flag once; thereafter, dismissInstall() owns it.
  const [declined, setDeclined] = useState<boolean>(() => safeSessionGet() === "1");
  const [isInstalled, setIsInstalled] = useState<boolean>(() => detectInstalled());

  useEffect(() => {
    const handler = (event: Event) => {
      // Already installed: don't prevent or capture. Chrome won't show a
      // banner for an installed app, and skipping preventDefault avoids the
      // "Banner not shown" console message for installed users.
      if (isInstalled) return;
      // Prevent the browser's default mini-infobar; we render our own.
      event.preventDefault();
      setInstallEvent((prev: InstallPromptEvent | null) => prev ?? (event as InstallPromptEvent));
    };
    const onAppInstalled = () => {
      setIsInstalled(true);
      setInstallEvent(null);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, [isInstalled]);

  // Chromium-only: detect an existing install from a plain browser tab, where
  // display-mode is "browser" and beforeinstallprompt can still fire.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const nav = window.navigator as Navigator & {
      getInstalledRelatedApps?: () => Promise<RelatedApplication[]>;
    };
    if (typeof nav.getInstalledRelatedApps !== "function") return;

    let cancelled = false;
    nav
      .getInstalledRelatedApps()
      .then(apps => {
        if (!cancelled && hasInstalledPwa(apps)) {
          setIsInstalled(true);
          setInstallEvent(null);
        }
      })
      .catch(() => {
        // API unavailable/denied: keep the display-mode result as fallback.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installEvent) return;
    try {
      await installEvent.prompt();
    } finally {
      // The event is single-use. Whether the user accepted or dismissed,
      // we clear it so we don't re-fire on the next render.
      setInstallEvent(null);
    }
  }, [installEvent]);

  const dismissInstall = useCallback(() => {
    safeSessionSet("1");
    setDeclined(true);
    setInstallEvent(null);
  }, []);

  return {
    isInstallable: installEvent !== null,
    isInstalled,
    declined,
    promptInstall,
    dismissInstall,
  };
}
